import React from 'react';
import { resolveMediaUrl } from '../services/api';

function SectionHeader({ title, count, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <h4 style={{ margin: 0, color: '#f3f4f6', fontSize: 15, fontWeight: 600 }}>
        {title}
        {typeof count === 'number' ? (
          <span style={{ color: '#9ca3af', marginLeft: 6 }}>({count})</span>
        ) : null}
      </h4>
      {action}
    </div>
  );
}

function EmptyState({ title, description, cta }) {
  return (
    <div
      style={{
        border: '1px dashed #374151',
        borderRadius: 10,
        padding: 16,
        background: '#0f1115'
      }}
    >
      <div style={{ color: '#d1d5db', fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 8 }}>{description}</div>
      {cta}
    </div>
  );
}

function AssetRow({ asset, type, onArchive }) {
  const label =
    asset?.name ||
    asset?.filename ||
    asset?.metadata?.title ||
    (asset?.url ? decodeURIComponent(asset.url.split('/').pop() || '') : `${type} asset`);

  const createdAt = asset?.createdAt
    ? new Date(asset.createdAt).toLocaleString()
    : asset?.created_at
      ? new Date(asset.created_at).toLocaleString()
      : '';

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: 12,
        borderRadius: 10,
        border: '1px solid #1f2937',
        background: '#0b0f17'
      }}
    >
      <div style={{ flex: 1, display: 'grid', gap: 6 }}>
        <div style={{ color: '#f9fafb', fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ color: '#9ca3af', fontSize: 12 }}>{createdAt}</div>
        {type === 'audio' && (
          <audio controls preload="metadata" src={asset.url} style={{ width: '100%' }} />
        )}
        {type === 'video' && (
          <video
            controls
            preload="metadata"
            src={asset.url}
            style={{ width: '100%', borderRadius: 8, maxHeight: 220 }}
          />
        )}
        {type === 'image' && (
          <img
            src={asset.thumbnailUrl || asset.url}
            alt={label}
            style={{ maxWidth: '100%', borderRadius: 10, border: '1px solid #1f2937' }}
            loading="lazy"
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <a
          href={resolveMediaUrl(asset.url)}
          target="_blank"
          rel="noreferrer"
          style={{
            background: '#1f2937',
            color: '#93c5fd',
            border: 0,
            borderRadius: 8,
            padding: '6px 12px',
            textDecoration: 'none',
            fontSize: 12,
            textAlign: 'center'
          }}
        >
          Open
        </a>
        {onArchive ? (
          <button
            onClick={() => onArchive(asset)}
            style={{
              background: '#111827',
              color: '#f87171',
              border: 0,
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12,
              cursor: 'pointer'
            }}
          >
            Archive
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ArchivedAssetRow({ asset, onRestore, onDelete }) {
  const label =
    asset?.name ||
    asset?.filename ||
    (asset?.url ? decodeURIComponent(asset.url.split('/').pop() || '') : 'Archived asset');
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: 12,
        borderRadius: 10,
        border: '1px solid #1f2937',
        background: '#0b0f17'
      }}
    >
      <div style={{ color: '#f9fafb', fontWeight: 500 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => onRestore(asset)}
          style={{
            background: '#065f46',
            color: '#d1fae5',
            border: 0,
            borderRadius: 8,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12
          }}
        >
          Restore
        </button>
        <button
          onClick={() => onDelete(asset)}
          style={{
            background: '#7f1d1d',
            color: '#fee2e2',
            border: 0,
            borderRadius: 8,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export function AssetGallery({
  assets,
  archived,
  loading = false,
  onArchive,
  onRestore,
  onDeleteArchived,
  onRefresh
}) {
  const sections = [
    { key: 'audio', title: 'Voices', empty: 'Record a fresh voiceover to unlock more automations.' },
    { key: 'images', title: 'Images', empty: 'Generate on-brand visuals to pair with your VO.' },
    { key: 'videos', title: 'Videos', empty: 'Combine voice + image to generate campaign videos.' }
  ];

  return (
    <div
      style={{
        background: '#111827',
        border: '1px solid #1f2937',
        borderRadius: 12,
        padding: 20,
        display: 'grid',
        gap: 20
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, color: '#f9fafb', fontSize: 18 }}>Project Assets</h3>
          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13 }}>
            Latest assets. Click to open or archive.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          style={{
            background: '#1f2937',
            color: '#e5e7eb',
            border: '1px solid #374151',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 13,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {sections.map((section) => {
        const list = Array.isArray(assets?.[section.key]) ? assets[section.key] : [];
        const latestOnly = list.slice(0, 1);
        return (
          <div key={section.key}>
            <SectionHeader title={section.title} count={list.length} />
            {list.length === 0 ? (
              <EmptyState
                title={`No ${section.title.toLowerCase()} yet`}
                description={section.empty}
                cta={
                  <span style={{ color: '#ff7a45', fontSize: 12 }}>
                    Use Quick Actions or Create Asset to get started.
                  </span>
                }
              />
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {latestOnly.map((asset) => (
                  <AssetRow
                    key={asset.id || asset.assetId || asset._id}
                    asset={asset}
                    type={
                      section.key === 'audio'
                        ? 'audio'
                        : section.key === 'images'
                          ? 'image'
                          : section.key === 'videos'
                            ? 'video'
                            : section.key
                    }
                    onArchive={onArchive}
                  />
                ))}
                {list.length > 1 ? (
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>
                    Showing latest {section.title.toLowerCase()}. See Archives to manage older items.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}

      <div>
        <SectionHeader
          title="Archived Assets"
          count={Array.isArray(archived) ? archived.length : 0}
          action={
            Array.isArray(archived) && archived.length ? (
              <span style={{ color: '#9ca3af', fontSize: 12 }}>
                Archived assets no longer appear in automations.
              </span>
            ) : null
          }
        />
        {!archived?.length ? (
          <EmptyState
            title="No archived items"
            description="Archive unused takes to keep your workspace tidy."
          />
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {archived.map((asset) => (
              <ArchivedAssetRow
                key={`archived-${asset.id || asset.assetId || asset._id}`}
                asset={asset}
                onRestore={onRestore}
                onDelete={onDeleteArchived}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssetGallery;
